const is = require('@sindresorhus/is');
const LibraryError = require('@nelnet-bank/lib-error');
require('crypto-js/lib-typedarrays'); // this is required for crypto-js to work
const CryptoJS = require('crypto-js/core');
const HmacSHA256 = require('crypto-js/hmac-sha256');
const SHA256 = require('crypto-js/sha256');
const Base64 = require('crypto-js/enc-base64');
const buffer = require('buffer');
const dateFormat = require('dateformat');
const BigInteger = require('./bigInteger');

const initN = 'FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD1'
  + '29024E088A67CC74020BBEA63B139B22514A08798E3404DD'
  + 'EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245'
  + 'E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED'
  + 'EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3D'
  + 'C2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F'
  + '83655D23DCA3AD961C62F356208552BB9ED529077096966D'
  + '670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B'
  + 'E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9'
  + 'DE2BCBF6955817183995497CEA956AE515D2261898FA0510'
  + '15728E5A8AAAC42DAD33170D04507A33A85521ABDF1CBA64'
  + 'ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7'
  + 'ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6B'
  + 'F12FFA06D98A0864D87602733EC86A64521F2B18177B200C'
  + 'BBE117577A615D6C770988C0BAD946E208E24FA074E5AB31'
  + '43DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF';

function hash(buf) {
  const str = buf instanceof Buffer ? CryptoJS.lib.WordArray.create(buf) : buf;
  const hashHex = SHA256(str).toString();

  return new Array(64 - hashHex.length).join('0') + hashHex;
}

function hexHash(hexStr) {
  return hash(Buffer.from(hexStr, 'hex'));
}

const N = new BigInteger(initN, 16);
const g = new BigInteger('2', 16);
const k = new BigInteger(
  hexHash(`00${N.toString(16)}0${g.toString(16)}`),
  16,
);

const infoBits = Buffer.from('Caldera Derived Key', 'utf8');

function randomBytes(nBytes) {
  return Buffer.from(CryptoJS.lib.WordArray.random(nBytes).toString(), 'hex');
}

function generateRandomSmallA() {
  const hexRandom = randomBytes(128).toString('hex');

  const randomBigInt = new BigInteger(hexRandom, 16);
  return randomBigInt.mod(N);
}

function getLargeAValue(smallAValue) {
  const A = g.modPow(smallAValue, N);

  // Ignoring this because it never gets hit. This was copied from AWS amplify
  /* istanbul ignore if */
  if (A.mod(N).equals(BigInteger.ZERO)) {
    throw new Error('Illegal parameter. A mod N cannot be 0.');
  }

  return A;
}

function padHex(bigInt) {
  let hashStr = bigInt.toString(16);
  // This else condition is only occasionally hit, so it will cause intermittent coverage failure
  /* istanbul ignore next */
  if (hashStr.length % 2 === 1) {
    hashStr = `0${hashStr}`;
  } else if ('89ABCDEFabcdef'.indexOf(hashStr[0]) !== -1) {
    hashStr = `00${hashStr}`;
  }

  return hashStr;
}

function calculateU(A, B) {
  const UHexHash = hexHash(padHex(A) + padHex(B));
  return new BigInteger(UHexHash, 16);
}

function calculateS(xValue, serverBValue, UValue, smallAValue) {
  const gModPowXN = g.modPow(xValue, N);

  const intValue2 = serverBValue.subtract(k.multiply(gModPowXN));
  const result = intValue2.modPow(
    smallAValue.add(UValue.multiply(xValue)),
    N,
  );

  return result.mod(N);
}

function computehkdf(ikm, salt) {
  const infoBitsWordArray = CryptoJS.lib.WordArray.create(
    Buffer.concat([
      infoBits,
      Buffer.from(String.fromCharCode(1), 'utf8'),
    ]),
  );

  // Ignoring this because it never hits the else condition, this is copied over from AWS amplify
  /* istanbul ignore next */
  const ikmWordArray = ikm instanceof Buffer ? CryptoJS.lib.WordArray.create(ikm) : ikm;
  /* istanbul ignore next */
  const saltWordArray = salt instanceof Buffer ? CryptoJS.lib.WordArray.create(salt) : salt;

  const prk = HmacSHA256(ikmWordArray, saltWordArray);
  const hmac = HmacSHA256(infoBitsWordArray, prk);
  return Buffer.from(hmac.toString(), 'hex').slice(0, 16);
}

function getPasswordAuthenticationKey(
  username,
  password,
  serverBValue,
  salt,
  largeAValue,
  smallAValue,
) {
  // Ignoring this because it never gets hit. This was copied from AWS amplify
  /* istanbul ignore if */
  if (serverBValue.mod(N).equals(BigInteger.ZERO)) {
    throw new Error('B cannot be zero.');
  }

  const UValue = calculateU(largeAValue, serverBValue);

  // Ignoring this because it never gets hit. This was copied from AWS amplify
  /* istanbul ignore if */
  if (UValue.equals(BigInteger.ZERO)) {
    throw new Error('U cannot be zero.');
  }

  const usernamePassword = `${username}:${password}`;
  const usernamePasswordHash = hash(usernamePassword);

  const xValue = new BigInteger(
    hexHash(padHex(salt) + usernamePasswordHash),
    16,
  );

  const sValue = calculateS(xValue, serverBValue, UValue, smallAValue);

  return computehkdf(
    Buffer.from(padHex(sValue), 'hex'),
    Buffer.from(padHex(UValue.toString(16)), 'hex'),
  );
}

const SRP = {
  /**
   * Secret Block must be passed in.
   */
  ERROR_MISSING_SECRET_BLOCK: Symbol('ERROR_MISSING_SECRET_BLOCK'),
  /**
   * Password must be passed in.
   */
  ERROR_MISSING_PASSWORD: Symbol('ERROR_MISSING_PASSWORD'),
  /**
   * User name must be passed in.
   */
  ERROR_MISSING_USERNAME: Symbol('ERROR_MISSING_USERNAME'),
  /**
   * SRP B must be passed in.
   */
  ERROR_MISSING_SRP_B: Symbol('ERROR_MISSING_SRP_B'),
  /**
   * Salt must be passed in.
   */
  ERROR_MISSING_SALT: Symbol('ERROR_MISSING_SALT'),

  getLargeAValue() {
    this.smallAValue = generateRandomSmallA();
    this.largeAValue = getLargeAValue(this.smallAValue);

    return {
      srpA: this.largeAValue.toString(16),
    };
  },

  getPasswordAuthenticationKey({
    username,
    password,
    srpB,
    salt,
    secretBlock,
  }) {
    // Validation
    if (!is.nonEmptyString(username)) {
      throw new LibraryError(SRP.ERROR_MISSING_USERNAME, 'Username must be set');
    }
    if (!is.nonEmptyString(password)) {
      throw new LibraryError(SRP.ERROR_MISSING_PASSWORD, 'Password must be set');
    }
    if (!is.nonEmptyString(srpB)) {
      throw new LibraryError(SRP.ERROR_MISSING_SRP_B, 'SRP B must be set');
    }
    if (!is.nonEmptyString(salt)) {
      throw new LibraryError(SRP.ERROR_MISSING_SALT, 'Salt must be set');
    }
    if (!is.nonEmptyString(secretBlock)) {
      throw new LibraryError(SRP.ERROR_MISSING_SECRET_BLOCK, 'Secret Block must be set');
    }

    const serverBValue = new BigInteger(srpB, 16);
    const serverSalt = new BigInteger(salt, 16);

    const hkdf = getPasswordAuthenticationKey(
      username,
      password,
      serverBValue,
      serverSalt,
      this.largeAValue,
      this.smallAValue,
    );

    const dateNow = dateFormat(new Date(), 'UTC:ddd mmm d HH:MM:ss "UTC" yyyy');

    const message = CryptoJS.lib.WordArray.create(
      buffer.Buffer.concat(
        [
          buffer.Buffer.from(username, 'utf8'),
          buffer.Buffer.from(secretBlock, 'base64'),
          buffer.Buffer.from(dateNow, 'utf8'),
        ],
      ),
    );

    const key = CryptoJS.lib.WordArray.create(hkdf);

    const signatureString = Base64.stringify(HmacSHA256(message, key));

    return {
      timestamp: dateNow,
      passwordClaimSignature: signatureString,
    };
  },
};

module.exports = SRP;
